/**
 * Caption / hashtag limits per platform, used by the public Post Preview tool.
 *
 * `hardLimit` is the documented maximum the platform accepts.
 * `truncateAt` is where the caption visually collapses behind "... more" in the
 * feed. Truncation is NOT an exact contract — platforms vary it by device width,
 * font size, and app version — so it is presented to users as an estimate.
 */
export interface PlatformLimit {
    id: 'instagram' | 'tiktok' | 'youtube-shorts'
    label: string
    /** Max caption/description characters accepted by the platform. */
    hardLimit: number
    /** Approximate character count shown before the caption is cut off in-feed. */
    truncateAt: number
    /** Max hashtags the platform counts; null when the platform sets no explicit cap. */
    maxHashtags: number | null
    /** Translation key for the extra guidance shown under the counters. */
    noteKey: string
}

export const PLATFORM_LIMITS: Record<PlatformLimit['id'], PlatformLimit> = {
    instagram: {
        id: 'instagram',
        label: 'Instagram',
        hardLimit: 2200,
        truncateAt: 125,
        maxHashtags: 30,
        noteKey: 'platformLimits.instagram',
    },
    tiktok: {
        id: 'tiktok',
        label: 'TikTok',
        hardLimit: 2200,
        truncateAt: 100,
        maxHashtags: null,
        noteKey: 'platformLimits.tiktok',
    },
    'youtube-shorts': {
        id: 'youtube-shorts',
        label: 'YouTube Shorts',
        hardLimit: 5000,
        truncateAt: 100,
        maxHashtags: 15,
        noteKey: 'platformLimits.youtubeShorts',
    },
}

export const PLATFORM_ORDER: PlatformLimit['id'][] = ['instagram', 'tiktok', 'youtube-shorts']
